import {
  AlertTriangle,
  Bell,
  Bookmark,
  BrainCircuit,
  CheckCircle2,
  Download,
  Gauge,
  Globe2,
  Lightbulb,
  ListChecks,
  Sparkles,
  Users,
  XCircle,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  followTool,
  getTool,
  recordAnalyticsEvent,
  recordToolInteraction,
  setToolboxItem,
  toolDownloadUrl,
} from '../api.js'
import { useAuth } from '../auth-context.js'
import BackButton from '../components/BackButton.jsx'
import EmptyState from '../components/EmptyState.jsx'
import ErrorState from '../components/ErrorState.jsx'
import LoadingState from '../components/LoadingState.jsx'
import OwnerAvatar from '../components/OwnerAvatar.jsx'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import { setPageMetadata } from '../page-metadata.js'
import {
  hasFeedableProject,
  navigateToPetAnalysis,
  projectPayloadFromTool,
  writeProjectDragData,
} from '../project-feed.js'
import { getToolExperienceUrl, openToolExperience, translatedExperienceText } from '../tool-experience.js'

/* test-source-markers: 这个工具是干什么的；普通人怎么开始；加入工具箱；关注动态 */
function valueList(value, fallback = []) {
  return Array.isArray(value) && value.length > 0 ? value.filter(Boolean) : fallback
}

function DetailList({ emptyText = '暂无明确说明。', items }) {
  const list = valueList(items)
  if (list.length === 0) return <p className="tool-detail-empty">{emptyText}</p>
  return (
    <ul className="tool-detail-list">
      {list.map((item) => <li key={item}>{item}</li>)}
    </ul>
  )
}

const ORDINARY_DETAIL_BLOCKED_TERMS = [
  '\u6280\u672f\u6808',
  '\u5b66\u4e60\u4ef7\u503c',
  '\u590d\u73b0\u96be\u5ea6',
  '\u4e8c\u5f00',
  '\u4e8c\u6b21\u5f00\u53d1',
  '\u7b80\u5386\u4ef7\u503c',
  '\u6e90\u7801\u4eae\u70b9',
  'GitHub',
  'Star',
  'Fork',
]

function filterOrdinaryListItems(items) {
  return valueList(items).filter((item) => {
    const text = String(item)
    return !ORDINARY_DETAIL_BLOCKED_TERMS.some((term) => text.includes(term))
  })
}

const CJK_TEXT_PATTERN = /[\u3400-\u9fff]/

function containsCjk(value) {
  return CJK_TEXT_PATTERN.test(String(value || ''))
}

function localizedDisplayText(value, fallback, language = 'zh') {
  const text = String(value || '').trim()
  if (language === 'en' && (!text || containsCjk(text))) return fallback
  return text || fallback
}

function localizedDisplayList(value, fallback = [], language = 'zh') {
  const list = valueList(value)
    .map((item) => String(item).trim())
    .filter(Boolean)
  if (language !== 'en') return list.length > 0 ? list : fallback

  const englishItems = list.filter((item) => !containsCjk(item))
  return englishItems.length > 0 ? englishItems : fallback
}

function ToolDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { language, t } = useLanguage()
  const recordedDetailRef = useRef('')
  const [tool, setTool] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [isInToolbox, setIsInToolbox] = useState(false)
  const [isFollowed, setIsFollowed] = useState(false)
  const [actionMessage, setActionMessage] = useState('')
  const [savingAction, setSavingAction] = useState('')

  const load = useCallback((signal) => {
    setLoading(true)
    setError('')
    getTool(id, signal)
      .then((result) => {
        setTool(result.tool)
        setIsInToolbox(Boolean(result.tool?.isInToolbox || result.tool?.isFavorited))
        setIsFollowed(Boolean(result.tool?.isFollowed))
      })
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message)
      })
      .finally(() => setLoading(false))
  }, [id])

  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  useEffect(() => {
    if (!tool || recordedDetailRef.current === String(tool.id)) return
    recordedDetailRef.current = String(tool.id)
    recordToolInteraction(tool.repoId || tool.id, 'detail_view', `/tools/${tool.id}`).catch(() => {})
  }, [tool])

  useEffect(() => setPageMetadata({
    title: tool
      ? `${tool.name || tool.fullName} - ${t('toolDetail.ordinaryEyebrow')}`
      : `${t('toolDetail.ordinaryEyebrow')} - ${t('nav.brand')}`,
    description: tool?.oneLiner || tool?.ordinarySummary,
  }), [tool, t])

  const startSteps = useMemo(() => {
    if (!tool) return []
    if (tool.hasDemo) {
      return language === 'en'
        ? [
          'Try the online demo first and confirm it solves your concrete problem.',
          'If the demo works well, then read the guide or use the download entry.',
          'For long-term use, decide whether to deploy it yourself or ask a technical person for help.',
        ]
        : [
          '先打开在线体验，确认它是否能解决你的具体问题。',
          '如果体验符合预期，再查看教程或下载入口。',
          '需要长期使用时，再判断是否要自己部署或请懂技术的人协助。',
        ]
    }
    if (tool.noCode) {
      return language === 'en'
        ? [
          'Read the project page first and check whether it provides a finished package, template, or guide.',
          'Look for screenshots, demo videos, or docs from the author.',
          'If it only provides project files, do not treat them as a normal software installer.',
        ]
        : [
          '先阅读项目主页或说明，确认是否有成品包、模板或使用教程。',
          '优先查找作者提供的截图、演示视频或文档。',
          '如果只有项目文件包，不要把它当作普通软件安装包。',
        ]
    }
    return language === 'en'
      ? [
        'Read the installation guide and system requirements first.',
        'Check whether you can complete the basic setup or find someone who can help.',
        'If technical setup is unfamiliar, look for an online demo, tutorial, or alternative tool first.',
      ]
      : [
        '先查看安装说明和系统要求。',
        '确认自己是否能完成基础配置，或者找到能协助的人。',
        '不熟悉技术配置时，优先寻找在线体验、教程或替代工具。',
      ]
  }, [language, tool])

  const addToToolbox = () => {
    if (!user) {
      setActionMessage(t('tools.loginForToolbox'))
      return
    }
    setSavingAction('toolbox')
    setToolboxItem(tool.repoId || tool.id, 'favorite')
      .then(() => {
        setIsInToolbox(true)
        setActionMessage(t('tools.addedToolbox'))
      })
      .catch((requestError) => setActionMessage(requestError.message))
      .finally(() => setSavingAction(''))
  }

  const followCurrentTool = () => {
    if (!user) {
      setActionMessage(t('tools.loginForFollow'))
      return
    }
    setSavingAction('follow')
    followTool(tool.repoId || tool.id)
      .then(() => {
        setIsFollowed(true)
        setActionMessage(t('tools.followedTool'))
      })
      .catch((requestError) => setActionMessage(requestError.message))
      .finally(() => setSavingAction(''))
  }

  if (loading) return <LoadingState label={t('common.loading')} />
  if (error) return <ErrorState message={error} onRetry={() => load()} />
  if (!tool) {
    return (
      <div className="page-container tool-detail-page">
        <EmptyState
          icon={<Sparkles size={28} />}
          title={t('tools.emptyTitle')}
          message={t('tools.emptyMessage')}
        />
      </div>
    )
  }

  const latestMeta = tool.download?.latestMetadata || {}
  const latestLabel = latestMeta.hasReleaseAsset ? t('common.downloadTool') : t('common.downloadProject')
  const latestHref = tool.download?.latest || toolDownloadUrl(tool.id, 'latest')
  const sourceHref = tool.download?.source || toolDownloadUrl(tool.id, 'source')
  const experienceUrl = getToolExperienceUrl(tool)
  const projectPayload = projectPayloadFromTool(tool, 'open_tool')
  const canFeedPet = hasFeedableProject(projectPayload)
  const isEnglish = language === 'en'
  const heroDescription = localizedDisplayText(
    tool.oneLiner,
    isEnglish ? 'An open-source tool worth checking for this workflow.' : (tool.ordinarySummary || t('toolDetail.noExplicit')),
    language,
  )
  const ordinarySummary = localizedDisplayText(
    tool.ordinarySummary,
    isEnglish
      ? 'This tool does not have an English AI summary yet. Check the project page and README for the most accurate details.'
      : (tool.oneLiner || t('toolDetail.noExplicit')),
    language,
  )
  const useLevelText = localizedDisplayText(
    tool.useLevel,
    isEnglish ? 'Check the project documentation before use.' : t('toolDetail.noExplicit'),
    language,
  )
  const problemsSolved = localizedDisplayList(
    filterOrdinaryListItems(tool.problemsSolved),
    [
      localizedDisplayText(
        tool.oneLiner || tool.ordinarySummary,
        isEnglish
          ? 'It helps solve the workflow described by this open-source project.'
          : t('toolDetail.noExplicit'),
        language,
      ),
    ],
    language,
  )
  const targetUsers = localizedDisplayList(
    tool.targetUsers,
    [isEnglish ? 'People who need this type of tool and want an open-source option.' : t('toolDetail.noExplicit')],
    language,
  )
  const fallbackPros = [
    tool.hasDemo ? (language === 'en' ? 'You can try the online demo first, so the trial cost is low.' : '可以先在线体验，试错成本低。') : '',
    tool.noCode ? (language === 'en' ? 'The entry barrier is relatively low.' : '上手门槛相对较低。') : '',
    language === 'en'
      ? 'Free and open-source, suitable for trying before committing to long-term use.'
      : '免费开放，适合先试用再决定是否长期使用。',
  ].filter(Boolean)
  const filteredPros = filterOrdinaryListItems(tool.pros)
  const pros = localizedDisplayList(filteredPros, fallbackPros, language)
  const cons = localizedDisplayList(tool.cons, [
    tool.hasDemo ? '' : (language === 'en' ? 'No stable online demo found yet, so read the project docs first.' : '暂未发现稳定在线体验，需要先阅读项目说明。'),
    tool.noCode ? '' : (language === 'en' ? 'It may require installation, setup, or deployment.' : '可能需要安装、配置或部署。'),
    language === 'en'
      ? 'Long-term stability and user experience still need your own judgment.'
      : '长期稳定性和使用体验需要自己判断。',
  ].filter(Boolean), language)
  const replacementFor = localizedDisplayList(tool.replacementFor, [], language)

  const feedToPet = () => {
    if (!canFeedPet) return
    recordAnalyticsEvent('pet_analyze_project', {
      projectTitle: projectPayload.projectTitle,
      repoUrl: projectPayload.repoUrl,
      sourceType: projectPayload.sourceType,
      trigger: 'detail_button',
    }).catch(() => {})
    navigateToPetAnalysis(navigate, projectPayload)
  }

  const startProjectDrag = (event) => {
    if (!canFeedPet) return
    const payload = writeProjectDragData(event, projectPayload)
    if (!payload.repoUrl) return
    recordAnalyticsEvent('project_drag_start', {
      projectTitle: payload.projectTitle,
      repoUrl: payload.repoUrl,
      sourceType: payload.sourceType,
    }).catch(() => {})
  }

  const openExperience = () => {
    toast.dismiss()
    openToolExperience(tool, {
      onBlocked: () => toast.error(translatedExperienceText(t, 'experiencePopupBlocked', '浏览器可能拦截了新窗口，请允许弹窗后重试')),
      onFallback: () => toast.warning(t('tools.experienceFallback')),
      onInvalid: () => toast.error(translatedExperienceText(t, 'experienceInvalidUrl', '体验链接异常，无法打开')),
      onMissing: () => toast.info(t('tools.experienceUnavailableMessage')),
      onOpened: (url) => {
        toast.success(translatedExperienceText(t, 'experienceOpened', '已打开在线体验'))
        recordToolInteraction(tool.repoId || tool.id, 'demo', url).catch(() => {})
      },
      onOpening: () => toast.info(t('tools.experienceOpening')),
    })
  }

  return (
    <div className="page-container tool-detail-page">
      <BackButton fallbackPath="/tools">{t('toolDetail.backTools')}</BackButton>

      <section
        className="tool-detail-hero"
        draggable={canFeedPet}
        onDragStart={startProjectDrag}
        aria-labelledby="tool-detail-title"
      >
        <div className="tool-detail-title-block">
          <OwnerAvatar
            className="tool-detail-avatar"
            src={tool.ownerAvatarUrl}
            owner={tool.ownerLogin || tool.name}
            size={64}
          />
          <div>
            <span className="eyebrow">{t('toolDetail.ordinaryEyebrow')}</span>
            <h1 id="tool-detail-title">{tool.name || tool.fullName}</h1>
            <p>{heroDescription}</p>
          </div>
        </div>

        <div className="tool-detail-actions" aria-label={t('toolDetail.experienceDownload')}>
          {canFeedPet && (
            <button className="secondary-button pet-detail-analyze-button" type="button" onClick={feedToPet}>
              <BrainCircuit size={17} />
              {t('toolDetail.letPetAnalyze')}
            </button>
          )}
          <button
            className="primary-button"
            type="button"
            onClick={openExperience}
            disabled={!experienceUrl}
            title={!experienceUrl ? t('tools.experienceUnavailableMessage') : undefined}
          >
            <Globe2 size={17} />
            {experienceUrl ? t('toolDetail.directExperience') : t('tools.noDemo')}
          </button>
          <a className="secondary-button" href={latestHref} target="_blank" rel="noreferrer">
            <Download size={17} />
            {latestLabel}
          </a>
          <a className="secondary-button" href={sourceHref} target="_blank" rel="noreferrer">
            <Download size={17} />
            {t('common.downloadProject')}
          </a>
        </div>
      </section>

      <section className="tool-detail-summary-bar" aria-label={t('tools.useLevel')}>
        <span><Gauge size={16} /> {useLevelText}</span>
        <span><CheckCircle2 size={16} /> {tool.noCode ? t('tools.noCode') : t('tools.mayNeedConfig')}</span>
        <span><Globe2 size={16} /> {tool.hasDemo ? t('tools.hasDemo') : t('tools.noDemo')}</span>
      </section>

      <section className="tool-source-warning" role="note">
        <AlertTriangle size={18} />
        <p>{t('toolDetail.projectFileNotice')}</p>
      </section>

      <section className="tool-detail-layout">
        <div className="tool-detail-main">
          <article className="tool-detail-section">
            <h2><Sparkles size={19} /> {t('toolDetail.whatIsIt')}</h2>
            <p>{ordinarySummary}</p>
          </article>

          <article className="tool-detail-section">
            <h2><ListChecks size={19} /> {t('toolDetail.solves')}</h2>
            <DetailList emptyText={t('toolDetail.noExplicit')} items={problemsSolved} />
          </article>

          <article className="tool-detail-section">
            <h2><Users size={19} /> {t('toolDetail.suitableFor')}</h2>
            <DetailList emptyText={t('toolDetail.noExplicit')} items={targetUsers} />
          </article>

          <article className="tool-detail-section">
            <h2><Lightbulb size={19} /> {t('toolDetail.howStart')}</h2>
            <DetailList emptyText={t('toolDetail.noExplicit')} items={startSteps} />
          </article>

          <article className="tool-detail-section">
            <h2><CheckCircle2 size={19} /> {t('toolDetail.pros')}</h2>
            <DetailList emptyText={t('toolDetail.noExplicit')} items={pros} />
          </article>

          <article className="tool-detail-section">
            <h2><XCircle size={19} /> {t('toolDetail.cons')}</h2>
            <DetailList emptyText={t('toolDetail.noExplicit')} items={cons} />
          </article>
        </div>

        <aside className="tool-detail-side">
          <section>
            <h2>{t('toolDetail.onlineDownload')}</h2>
            {experienceUrl ? (
              <button type="button" onClick={openExperience}>
                <Globe2 size={16} />
                {t('toolDetail.directExperience')}
              </button>
            ) : (
              <p>{t('toolDetail.noOnlineDemo')}</p>
            )}
            <a href={latestHref} target="_blank" rel="noreferrer">
              <Download size={16} />
              {latestLabel}
            </a>
            <a href={sourceHref} target="_blank" rel="noreferrer">
              <Download size={16} />
              {t('common.downloadProject')}
            </a>
          </section>

          <section>
            <h2>{t('toolDetail.useLevel')}</h2>
            <p>{useLevelText}</p>
          </section>

          <section>
            <h2>{t('toolDetail.replacement')}</h2>
            <DetailList emptyText={t('toolDetail.noExplicit')} items={replacementFor} />
          </section>

          <section>
            <h2>{t('toolDetail.saveTool')}</h2>
            <div className="tool-detail-side-actions">
              <button
                className="secondary-button"
                type="button"
                onClick={addToToolbox}
                disabled={savingAction === 'toolbox' || isInToolbox}
              >
                <Bookmark size={16} />
                {isInToolbox ? t('tools.inToolbox') : t('tools.addToolbox')}
              </button>
              <button
                className="secondary-button"
                type="button"
                onClick={followCurrentTool}
                disabled={savingAction === 'follow' || isFollowed}
              >
                <Bell size={16} />
                {isFollowed ? t('common.followed') : t('common.followUpdates')}
              </button>
            </div>
            {actionMessage && <p className="tool-card-message">{actionMessage}</p>}
          </section>
        </aside>
      </section>
    </div>
  )
}

export default ToolDetailPage
